import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { filter, map, merge, skip, startWith, switchMap } from "rxjs";
import { environment } from "../../../../../../environments/environment";
import { userContextStore } from "../../../../../shared/data/user-context.store";
import { CoreMediaNavigationClient } from "../../../navigation/data/coremedia-navigation.client";
import { CoreMediaContentRendererComponent } from "../coremedia-content-renderer/coremedia-content-renderer";
import { NotFoundPageComponent } from "../not-found/not-found-page";

/** Discriminated state for the async path -> content-ID resolution. */
type ContentIdState =
  | { status: "loading" }
  | { status: "resolved"; id: string | null };

/**
 * Renders pages from the footer legal-links menu (Datenschutz, Nutzungsbedingungen, Impressum),
 * which live in their own CoreMedia site (`environment.footerLegalLinks`) — separate from the
 * current navigation channel's site. Content is loaded resolved to a single locale, the same way
 * `NotFoundPageComponent` renders the 404 channel.
 */
@Component({
  selector: "claas-legal-page",
  template: `
    @if (showNotFound()) {
      <claas-not-found-page />
    } @else if (contentId(); as id) {
      <claas-coremedia-content-renderer
        [contentId]="id"
        [localized]="true"
        [siteName]="legalSiteName"
      />
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CoreMediaContentRendererComponent, NotFoundPageComponent],
})
export class LegalPageComponent {
  private readonly router = inject(Router);
  private readonly navigationClient = inject(CoreMediaNavigationClient);
  private readonly localeStore = inject(userContextStore);

  protected readonly legalSiteName = environment.footerLegalLinks.siteName;

  /** Emits when locale is changed after initial load, so current path is revalidated. */
  private readonly localeChanged$ = toObservable(
    computed(() => ({
      status: this.localeStore.status(),
      country: this.localeStore.country(),
      language: this.localeStore.language(),
    })),
  ).pipe(
    skip(1),
    filter((locale) => locale.status === "ready"),
  );

  private readonly contentIdState = toSignal(
    merge(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
      ),
      this.localeChanged$,
    ).pipe(
      startWith(null),
      switchMap(() => {
        const fullPath = (this.router.url || "/").split("?")[0] || "/";
        // Strip the mandatory "legal" prefix segment (see legalPathMatcher in app.routes.ts) —
        // the backend resolves the original, speaking site path without it.
        const contentPath = fullPath.replace(/^\/legal(?=\/|$)/, "") || "/";
        return this.navigationClient
          .resolveLegalContentIdByPath(contentPath)
          .pipe(
            map((id): ContentIdState => ({ status: "resolved", id })),
            startWith<ContentIdState>({ status: "loading" }),
          );
      }),
    ),
    { initialValue: { status: "loading" } as ContentIdState },
  );

  readonly contentId = computed(() => {
    const state = this.contentIdState();
    return state.status === "resolved" ? state.id : null;
  });

  /** Unknown legal paths (path resolution -> null) show the 404 page. */
  readonly showNotFound = computed(() => {
    const state = this.contentIdState();
    return state.status === "resolved" && state.id === null;
  });
}
